import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, Sparkles, FileDown, ArrowRight,
  Zap, Layers, Building2, GitBranch,
  Lock, Shield, AlertTriangle, TrendingUp, Calculator,
  Plus, Trash2, Info,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.13aq — Strategic Planning Institute · Founding Partner bespoke page.
//
// Built for the Burditt Krost conversation (Owner, Strategic Planning Institute
// P/L, Perth WA). Origin: he ran the Hodgson Group electricity consolidation
// manually — exactly DARA's wedge, but for ONE cost line.
//
// Framing: founding-partner channel deal, not a customer pitch. 30% recurring
// commission for life on every workspace he brings, with a 50% Y1 anchor on
// the first 3 clients. Capped at 5 founding partners globally.
//
// Holding URL — share with Burditt ONLY after the in-person conversation lands.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ON_THEN_NOW = [
  {
    Icon: Zap,
    label: "Electricity · the Hodgson engagement",
    burditt: "Walked every subsidiary's account into a single name. Audited tariffs across the group. Built a coordinated tender across WA retailers. Cost centre into profit centre. The kind of discipline most consultants do not have the patience to carry through.",
    dara: "DARA ingests every electricity NMI across subsidiaries in 24 hours. Benchmarks tariffs against the live AU energy index. Flags retender windows continuously. The same method, applied at software speed.",
  },
  {
    Icon: Building2,
    label: "Telco · mobile fleet",
    burditt: "Out of scope for the Hodgson engagement, but the same waste pattern lives in most groups: redundant lines, expired bundle credits, off-tariff legacy plans accumulating quietly over years.",
    dara: "Consolidates every plan across subsidiaries. Detects dormant lines, overlapping bundles, off-tariff legacy contracts. Models group vs. per-entity tender savings.",
  },
  {
    Icon: Layers,
    label: "SaaS · vendor spend",
    burditt: "Notoriously hard to audit across a group — vendors invoice subsidiaries separately, finance teams rarely compare line items across entities.",
    dara: "Surfaces duplicate subscriptions across subsidiaries (Xero, Microsoft, payroll, insurance). Maps cross-entity overlap. One reconciled vendor ledger.",
  },
  {
    Icon: GitBranch,
    label: "Banking · merchant fees",
    burditt: "A multi-month engagement on its own — bank reconciliation per subsidiary, then tariff modelling against benchmarks. A standalone project for a separate consulting brief.",
    dara: "Reads each entity's transaction stream read-only. Benchmarks merchant fees, FX margins, and SaaS payment costs. Flags renegotiation thresholds.",
  },
];

// Hodgson-style rolled-up demo data — illustrative not actual. Six fictional
// entities under a parent group. Numbers are realistic for a WA-based
// diversified group with mixed property + agribusiness + services subsidiaries.
// Phase 25.13ar — Group Rollup is now an interactive sandbox driven by the
// partner's own numbers rather than DARA-supplied Hodgson-style fixtures.
// The static DEMO_GROUP constant has been removed in favour of <GroupRollupSandbox/>.
// Pre-seed rows are deliberately generic ("Subsidiary A/B/C") and tuned so
// the default benchmark (A$18,000/site/year) classifies them across three
// different flag tones — educational, not catastrophic. The partner
// overwrites them with real client data the moment the conversation
// gets specific.
const SANDBOX_SEED_ROWS = [
  { id: "seed-a", name: "Subsidiary A", sites: 3, spend: 72_000 },   // +33 % · TENDER WINDOW
  { id: "seed-b", name: "Subsidiary B", sites: 2, spend: 42_000 },   // +17 % · TARIFF MISMATCH
  { id: "seed-c", name: "Subsidiary C", sites: 1, spend: 18_000 },   //   0 % · IN LINE WITH BENCHMARK
];

// Benchmark slider bounds — A$/site/year for WA mid-market commercial electricity.
// Default tuned to A$18k/site/year (≈ A$1,500/mo per site, realistic for a
// mid-load commercial connection on a market retail tariff in 2026). The
// partner drags this to match their own domain view.
const BENCHMARK_MIN = 8_000;
const BENCHMARK_MAX = 30_000;
const BENCHMARK_DEFAULT = 18_000;

const ECONOMICS_ROWS = [
  { label: "Commission rate (recurring)", standard: "30 % of MRR · for life of customer", anchor: "50 % of MRR · Year 1 only · first 3 clients" },
  { label: "Duration", standard: "Unlimited · paid every month the workspace is active", anchor: "First 12 months only · drops to 30 % from month 13" },
  { label: "Reset / clawback", standard: "Zero clawback after month 6 of customer life", anchor: "Pro-rated refund only if customer churns inside first 60 days" },
  { label: "Who owns the customer", standard: "DARA owns the customer relationship · partner stays in the loop on every customer", anchor: "Same as standard — partner is never substituted out" },
  { label: "White-label", standard: "Co-branded PDFs · 'Powered by DARA · Curated by Strategic Planning Institute'", anchor: "Same as standard" },
  { label: "Quota / minimum", standard: "None · run your practice your way", anchor: "None" },
];

const HONEST_LIMITS = [
  { Icon: AlertTriangle, label: "We will not substitute the partner out. Once Burditt brings a workspace, he stays on every renewal forever — even if DARA grows a direct sales team. The contract makes this irrevocable.", colour: "text-[#FFB000]" },
  { Icon: Lock, label: "Multi-region sovereign: AU (AWS Sydney) · UK (London) · US (us-east-1). Data never crosses regions. Hodgson-tier clients can demand strict AU residency.", colour: "text-[#00E676]" },
  { Icon: Shield, label: "Founding partner roster is capped at 5 globally. Each partner gets their own territory framing (verticals OR geographies). Burditt's first-mover slot covers WA mid-market + Aus diversified groups.", colour: "text-[#00E676]" },
  { Icon: CheckCircle2, label: "DARA does not compete with the partner's consulting work. Strategy, methodology design, client judgement — they all stay with Strategic Planning Institute. DARA is the data engine that sits underneath the relationship.", colour: "text-[#00E676]" },
];

const NEXT_STEPS = [
  { code: "01", title: "Sign Founding Partner agreement", body: "One page, written in plain English. Locks in 30 % for life + 50 % Y1 anchor on first 3 clients. Mutual termination only with 90 days' notice." },
  { code: "02", title: "Onboard first client (Hodgson Group)", body: "Burditt makes the introduction. DARA scopes one cost line in 24h, runs the 90-day rollup engagement, delivers a co-branded quarterly brief. First 50 % commission lands month 2." },
  { code: "03", title: "Scale to 5 clients over 12 months", body: "Burditt's existing 500+ LinkedIn connections are the addressable network. At 5 clients (achievable), recurring monthly commission is ~$15k AUD. Compounding from there." },
];

function fmtAud(n) {
  return `A$${Math.round(n).toLocaleString("en-AU")}`;
}

export default function StrategicPlanningInstitute() {
  usePageview("/strategic-planning-institute");
  useNoIndex();

  const [form, setForm] = useState({ email: "", name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Strategic Planning Institute P/L",
        role: "Founding Partner candidate",
        message: [
          form.phone ? `Phone: ${form.phone}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "strategic-planning-institute",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — Emmanuel will reach out personally.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const partnerBriefUrl = `${API}/public/partner-program-brief`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="spi-page">
      <BespokePageRibbon
        prospectName="Strategic Planning Institute · Burditt Krost"
        muted
      />
      <BespokeWatermark pageLabel="SPI · BURDITT" position="top" />
      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="spi-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
              · STRATEGIC PLANNING INSTITUTE × DARA · FOUNDING PARTNER
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={partnerBriefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="spi-nav-download-brief"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Partner Program PDF
            </a>
            <a
              href="#engage"
              data-testid="spi-nav-engage"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90"
            >
              Lock the slot
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFB000]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // BESPOKE PROPOSAL · PREPARED FOR BURDITT KROST · STRATEGIC PLANNING INSTITUTE P/L · {new Date().toLocaleString("en-AU", { month: "short", year: "numeric" }).toUpperCase()}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-3">
            STRATEGIC PLANNING INSTITUTE <span className="text-[#FFB000]">×</span> DARA OS
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-5xl">
            You showed it works with electricity. <span className="text-[#FFB000]">DARA carries the same discipline across every cost line, every quarter, every client.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-3xl leading-relaxed">
            The Hodgson Group consolidation — accounts under a single name, tariffs audited, group tender run, cost centre into profit centre — is exactly the kind of work that takes years of judgement to do well. DARA OS exists because of methods like yours: <span className="text-white font-bold">an instrument that handles the data plumbing</span> across every cost line, every entity, every quarter, so the strategic call stays where it belongs — with you. This is a <span className="text-[#FFB000]">founding-partner conversation</span>, not a customer pitch.
          </p>

          <div className="flex flex-wrap gap-2 mt-7" data-testid="spi-hero-meta-pills">
            {["30 % RECURRING · FOR LIFE", "50 % ANCHOR · FIRST 3 CLIENTS", "5 FOUNDING PARTNER SLOTS · GLOBALLY", "WHITE-LABEL", "AU SOVEREIGN", "DARA ENGINE"].map(p => (
              <span key={p} className="px-2.5 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5 font-mono text-[10px] uppercase tracking-widest">{p}</span>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="spi-hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Lock your founding-partner slot <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={partnerBriefUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="spi-hero-download-brief"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <FileDown className="w-3.5 h-3.5" /> Download the Partner Program brief
            </a>
          </div>
        </div>
      </section>

      {/* Wedge */}
      <section className="border-b border-zinc-900 bg-[#FFB000]/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// THE WEDGE · ONE PARAGRAPH</div>
          <p className="text-xl sm:text-2xl font-heading font-bold leading-snug max-w-5xl tracking-tight">
            <span className="text-[#FFB000]">You bring</span> a client like Hodgson — one introduction, one trusted recommendation. <span className="text-[#FFB000]">DARA carries</span> the data work in the same disciplined order you have always brought to it: every cost line, audit-defensible, refreshed every quarter, without you having to staff it. <span className="text-zinc-400">Strategy stays with you. The data engine sits underneath. You earn 30 % of every dollar that workspace pays DARA — every month, for as long as the client stays.</span>
          </p>
        </div>
      </section>

      {/* Then & Now — Burditt's manual method vs DARA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// THE METHOD · CARRIED FORWARD</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          The discipline you brought to Hodgson. <span className="text-[#FFB000]">Carried forward, every cost line, every quarter.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="spi-then-now-grid">
          {ON_THEN_NOW.map(({ Icon, label, burditt, dara }) => (
            <div key={label} className="bg-black p-6">
              <Icon className="w-5 h-5 text-[#FFB000] mb-3" />
              <div className="font-heading font-bold text-base tracking-tight mb-3">{label}</div>
              <div className="space-y-3 text-xs">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">BURDITT · THE METHOD</div>
                  <p className="text-zinc-400 leading-relaxed">{burditt}</p>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">DARA · THE ENGINE UNDERNEATH</div>
                  <p className="text-zinc-200 leading-relaxed">{dara}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Group Rollup sandbox — interactive, runs from his numbers */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// LIVE-ENGINE SANDBOX · RUN YOUR OWN NUMBERS</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            Put a real client&apos;s numbers in. <span className="text-[#FFB000]">Watch the engine generate flags and a savings band from your data.</span>
          </h2>
          <p className="text-sm text-zinc-400 mt-3 max-w-3xl leading-relaxed">
            Type a subsidiary name, the number of sites, and your honest estimate of their last 12 months&apos; electricity spend. Drag the benchmark slider until DARA&apos;s &quot;fair WA tariff&quot; figure matches your domain view. DARA computes the variance, classifies the flag, and rewrites the brief paragraph live. <span className="text-zinc-500">All numbers stay on this device — nothing is sent or stored anywhere.</span>
          </p>
          <GroupRollupSandbox />
        </div>
      </section>

      {/* Partner economics */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// PARTNER ECONOMICS · WRITTEN IN PLAIN ENGLISH</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          30 % recurring for life. <span className="text-[#FFB000]">50 % Year 1 anchor on the first 3 clients.</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl leading-relaxed">
          One commercial structure, no hidden tiers. Every workspace Burditt brings to DARA pays its monthly subscription as normal — Burditt receives a fixed share of that revenue, every month, for as long as the workspace stays active. The anchor bonus is a founding-partner-only sweetener tied to the first 3 clients.
        </p>

        <div className="mt-10 border border-zinc-900 overflow-x-auto" data-testid="spi-economics-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <th className="text-left p-4 border-b border-zinc-900 w-1/4">Term</th>
                <th className="text-left p-4 border-b border-zinc-900">Standard (clients 4+)</th>
                <th className="text-left p-4 border-b border-zinc-900 bg-[#FFB000]/5 text-[#FFB000]">Anchor bonus (first 3 clients)</th>
              </tr>
            </thead>
            <tbody>
              {ECONOMICS_ROWS.map((r) => (
                <tr key={r.label} className="border-b border-zinc-900">
                  <td className="p-4 text-zinc-300 font-mono text-[11px] uppercase tracking-wide">{r.label}</td>
                  <td className="p-4 text-zinc-300 leading-relaxed">{r.standard}</td>
                  <td className="p-4 text-zinc-100 leading-relaxed bg-[#FFB000]/[0.025]">{r.anchor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Worked example */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="spi-economics-examples">
          <ExampleCard
            title="One client (Hodgson)"
            sub="Workspace fee"
            burditt_y1={1750}
            burditt_y2={1050}
            dara_y1={1750}
            dara_y2={2450}
            highlight
          />
          <ExampleCard
            title="Five clients"
            sub="Workspace fee each"
            burditt_y1={6300}
            burditt_y2={5250}
            dara_y1={11200}
            dara_y2={12250}
          />
          <ExampleCard
            title="Twenty clients"
            sub="Workspace fee each"
            burditt_y1={21000}
            burditt_y2={21000}
            dara_y1={49000}
            dara_y2={49000}
            longterm
          />
        </div>
      </section>

      {/* Live commission calculator — interactive in-meeting tool */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// LIVE CALCULATOR · POWERED BY DARA&apos;S ACTUAL COMMISSION ENGINE</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Drag the sliders. <span className="text-[#FFB000]">Find the unfair scenario.</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-3xl leading-relaxed">
          Math below mirrors the production commission engine bit-for-bit. Same rules. No marketing rounding. <span className="text-zinc-500">Anchor bonus auto-applies to the first 3 clients in Year 1 only — toggle it off to model the post-anchor steady state.</span>
        </p>
        <PartnerCalculator />
      </section>

      {/* Honest limits */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// WHAT WE WILL NEVER DO</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Founding partners are protected. <span className="text-[#FFB000]">Forever-locked.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="spi-honest-limits">
          {HONEST_LIMITS.map(({ Icon, label, colour }, i) => (
            <div key={i} className="bg-black p-6 flex items-start gap-3">
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colour}`} />
              <p className="text-sm text-zinc-300 leading-relaxed">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Next steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">// NEXT STEPS · 12-MONTH HORIZON</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
          Three steps. <span className="text-[#FFB000]">No board meeting required.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="spi-next-steps">
          {NEXT_STEPS.map(({ code, title, body }) => (
            <div key={code} className="bg-black p-6">
              <div className="font-heading font-black text-5xl tracking-tighter text-[#FFB000] mb-2">{code}</div>
              <div className="font-heading font-bold text-lg tracking-tight">{title}</div>
              <p className="text-xs text-zinc-300 leading-relaxed mt-3">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Engage */}
      <section id="engage" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-b border-zinc-900">
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.03] p-6 sm:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-3">// LOCK THE FOUNDING PARTNER SLOT</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl">
            5 founding-partner slots. <span className="text-[#FFB000]">First-mover terms.</span>
          </h2>
          <p className="text-sm text-zinc-300 mt-3 max-w-3xl leading-relaxed">
            This page is a holding artefact for the in-person conversation. After Burditt and Emmanuel meet next week, a one-page Founding Partner agreement gets signed (PDF, plain English, mutual termination clause). First client introduction can follow within the same week. <span className="text-zinc-400">Submit below if you want to circulate the agreement draft in advance.</span>
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="spi-engage-form">
              <Input label="Email *" testid="spi-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Full name" testid="spi-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Phone (optional)" testid="spi-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Anything to flag in advance (optional)</span>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="spi-message"
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                    placeholder="e.g. preferred first-client introduction · sectors of focus · territory framing · etc."
                  />
                </label>
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="font-mono text-[10px] text-zinc-600 max-w-md">
                  By submitting you accept that Emmanuel (DARA founder) will reach out personally about the founding-partner agreement.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="spi-submit"
                  className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40"
                >
                  {submitting ? <><Sparkles className="w-3.5 h-3.5 animate-pulse" /> SENDING…</> : <><ChevronRight className="w-3.5 h-3.5" /> Send to DARA founder</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6" data-testid="spi-success">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676] mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                REQUEST RECEIVED
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">Slot held.</h3>
              <p className="text-sm text-zinc-300 mt-2 max-w-xl leading-relaxed">
                Emmanuel will reach out within 24 hours. Meanwhile feel free to share the <a href={partnerBriefUrl} target="_blank" rel="noopener noreferrer" className="text-[#FFB000] underline">Founding Partner Program brief</a>.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Documents</div>
            <ul className="space-y-2">
              <li><a href={partnerBriefUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founding Partner brief</a></li>
              <li><a href={`${API}/public/overview-one-pager`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">DARA 1-Pager</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Related</div>
            <ul className="space-y-2">
              <li><Link to="/founders-guide" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Founder's Guide</Link></li>
              <li><Link to="/pricing" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Pricing</Link></li>
              <li><Link to="/" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Homepage</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Trust</div>
            <ul className="space-y-2">
              <li><Link to="/security" className="text-[11px] text-zinc-400 hover:text-[#FFB000]">Security & Trust</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              The Bloomberg-terminal for enterprise operators. Built in Australia, deployed worldwide.
            </p>
            <p className="text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Bespoke proposal · prepared for Burditt Krost · Strategic Planning Institute P/L · not for redistribution.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700">SPI × DARA · v1</span>
        </div>
      </footer>
      <BespokeWatermark pageLabel="SPI · BURDITT" position="bottom" />
    </div>
  );
}

function PartnerCalculator() {
  const [clients, setClients] = useState(5);
  const [mrr, setMrr] = useState(3500);
  const [anchorActive, setAnchorActive] = useState(true);

  const anchorClients = anchorActive ? Math.min(3, clients) : 0;
  const standardClients = Math.max(0, clients - anchorClients);

  // Match production tier policy exactly: 50% on anchor in Y1, 30% everywhere else.
  const y1_monthly_burditt = anchorClients * mrr * 0.50 + standardClients * mrr * 0.30;
  const y2_monthly_burditt = clients * mrr * 0.30;
  const total_monthly_revenue = clients * mrr;
  const y1_monthly_dara = total_monthly_revenue - y1_monthly_burditt;
  const y2_monthly_dara = total_monthly_revenue - y2_monthly_burditt;

  const y1_annual_burditt = y1_monthly_burditt * 12;
  const y2_annual_burditt = y2_monthly_burditt * 12;

  // 5-year cumulative assuming each workspace persists. Y1 anchor pricing → Y2-5 standard.
  const five_year_burditt = y1_annual_burditt + (y2_annual_burditt * 4);

  return (
    <div className="mt-10 border border-zinc-900 bg-[#0a0a0a]" data-testid="spi-calculator">
      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border-b border-zinc-900">
        {/* Clients slider */}
        <div className="bg-black p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// CLIENTS BROUGHT TO DARA</div>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="font-heading font-black text-5xl tracking-tighter text-[#FFB000]">{clients}</span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
              {clients === 1 ? "workspace" : "workspaces"}
            </span>
          </div>
          <input
            type="range"
            min={1}
            max={20}
            step={1}
            value={clients}
            onChange={(e) => setClients(Number(e.target.value))}
            data-testid="spi-calc-clients"
            className="w-full accent-[#FFB000] cursor-pointer"
            aria-label="Number of clients"
          />
          <div className="flex justify-between font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
            <span>1</span><span>5</span><span>10</span><span>20</span>
          </div>
        </div>
        {/* MRR slider */}
        <div className="bg-black p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// MRR PER WORKSPACE · AUD</div>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="font-heading font-black text-5xl tracking-tighter text-[#FFB000]">A${mrr.toLocaleString("en-AU")}</span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">/mo</span>
          </div>
          <input
            type="range"
            min={1000}
            max={10000}
            step={500}
            value={mrr}
            onChange={(e) => setMrr(Number(e.target.value))}
            data-testid="spi-calc-mrr"
            className="w-full accent-[#FFB000] cursor-pointer"
            aria-label="MRR per workspace"
          />
          <div className="flex justify-between font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
            <span>A$1k</span><span>A$3.5k</span><span>A$7k</span><span>A$10k</span>
          </div>
        </div>
        {/* Anchor toggle */}
        <div className="bg-black p-6 flex flex-col justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// ANCHOR BONUS · FIRST 3 CLIENTS · Y1</div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className={`font-heading font-black text-5xl tracking-tighter ${anchorActive ? "text-[#FFB000]" : "text-zinc-700"}`}>
                {anchorActive ? "ON" : "OFF"}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                +20 % Y1 boost
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAnchorActive(!anchorActive)}
            data-testid="spi-calc-anchor-toggle"
            className={`font-mono text-[10px] uppercase tracking-widest px-4 py-2 border transition-colors ${
              anchorActive
                ? "bg-[#FFB000] text-black border-[#FFB000]"
                : "bg-transparent text-zinc-400 border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000]"
            }`}
          >
            {anchorActive ? "Anchor active · click to remove" : "Anchor off · click to apply"}
          </button>
        </div>
      </div>

      {/* Live outputs · Burditt's earnings */}
      <div className="p-6 sm:p-8" data-testid="spi-calc-output">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4 flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5" /> // BURDITT&apos;S EARNINGS
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          <CalcCell label="YEAR 1 · MONTHLY" value={`A$${Math.round(y1_monthly_burditt).toLocaleString("en-AU")}`} highlight />
          <CalcCell label="YEAR 1 · ANNUAL" value={`A$${Math.round(y1_annual_burditt).toLocaleString("en-AU")}`} />
          <CalcCell label="YEAR 2+ · MONTHLY" value={`A$${Math.round(y2_monthly_burditt).toLocaleString("en-AU")}`} />
          <CalcCell label="5-YR CUMULATIVE" value={`A$${Math.round(five_year_burditt).toLocaleString("en-AU")}`} cumulative />
        </div>

        {anchorClients > 0 && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-4">
            // {anchorClients} of {clients} {clients === 1 ? "client" : "clients"} anchor-eligible (first 3) · {standardClients} at standard 30 %
          </p>
        )}

        {/* DARA economics — fairness signal */}
        <div className="mt-8 pt-6 border-t border-zinc-900">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">// DARA&apos;S SIDE OF THE DEAL · TRANSPARENCY</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            <CalcCell label="DARA Y1 · MONTHLY" value={`A$${Math.round(y1_monthly_dara).toLocaleString("en-AU")}`} muted />
            <CalcCell label="DARA Y1 · ANNUAL" value={`A$${Math.round(y1_monthly_dara * 12).toLocaleString("en-AU")}`} muted />
            <CalcCell label="DARA Y2+ · MONTHLY" value={`A$${Math.round(y2_monthly_dara).toLocaleString("en-AU")}`} muted />
            <CalcCell label="DARA Y2+ · ANNUAL" value={`A$${Math.round(y2_monthly_dara * 12).toLocaleString("en-AU")}`} muted />
          </div>
          <p className="text-xs text-zinc-500 mt-4 max-w-3xl leading-relaxed">
            Total workspace revenue at this scale: <span className="text-zinc-200 font-mono">A${total_monthly_revenue.toLocaleString("en-AU")}/mo</span> · <span className="text-zinc-200 font-mono">A${(total_monthly_revenue * 12).toLocaleString("en-AU")}/yr</span>. DARA absorbs all fulfilment, hosting, support, and product development costs from its share — partner workload after introduction is zero.
          </p>
        </div>
      </div>
    </div>
  );
}

function CalcCell({ label, value, highlight, cumulative, muted }) {
  return (
    <div className={`p-5 ${highlight ? "bg-[#FFB000]/8" : cumulative ? "bg-[#FFB000]/4" : "bg-black"}`}>
      <div className={`font-mono text-[9px] uppercase tracking-widest mb-2 ${muted ? "text-zinc-600" : "text-zinc-500"}`}>
        // {label}
      </div>
      <div
        className={`font-heading font-black tracking-tight ${
          highlight ? "text-[#FFB000] text-2xl"
            : cumulative ? "text-[#FFB000] text-xl"
            : muted ? "text-zinc-400 text-base"
            : "text-white text-xl"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SummaryCell({ label, value, accent, highlight }) {
  return (
    <div className={`bg-black p-5 ${highlight ? "bg-[#FFB000]/5" : ""}`}>
      <div className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${accent ? "text-[#FFB000]" : "text-zinc-500"}`}>
        // {label}
      </div>
      <div className={`font-heading font-bold tracking-tight ${highlight ? "text-[#FFB000] text-base" : "text-white text-lg"}`}>
        {value}
      </div>
    </div>
  );
}

function ExampleCard({ title, sub, burditt_y1, burditt_y2, dara_y1, dara_y2, highlight, longterm }) {
  return (
    <div className={`p-6 ${highlight ? "bg-[#FFB000]/5" : "bg-black"}`} data-testid={`spi-example-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading font-bold text-lg tracking-tight">{title}</h3>
        {highlight && <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/40 bg-[#FFB000]/10 px-1.5 py-0.5">FIRST CLIENT</span>}
        {longterm && <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 border border-zinc-800 bg-zinc-900 px-1.5 py-0.5">12-MO HORIZON</span>}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mb-4">{sub}</div>
      <div className="space-y-3 text-xs">
        <div className="flex justify-between items-baseline border-b border-zinc-900 pb-2">
          <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Burditt · Y1</span>
          <span className="font-heading font-bold text-[#FFB000] text-base">A${burditt_y1.toLocaleString("en-AU")}/mo</span>
        </div>
        <div className="flex justify-between items-baseline border-b border-zinc-900 pb-2">
          <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Burditt · Y2+</span>
          <span className="font-heading font-bold text-zinc-200">A${burditt_y2.toLocaleString("en-AU")}/mo</span>
        </div>
        <div className="flex justify-between items-baseline border-b border-zinc-900 pb-2">
          <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">DARA · Y1</span>
          <span className="font-mono text-zinc-400">A${dara_y1.toLocaleString("en-AU")}/mo</span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">DARA · Y2+</span>
          <span className="font-mono text-zinc-400">A${dara_y2.toLocaleString("en-AU")}/mo</span>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group Rollup Sandbox — Phase 25.13ar
//
// Interactive replacement for the static Hodgson-style demo. The partner
// types subsidiary structure + last-12-months electricity spend, drags a
// benchmark-per-site slider to match their domain view, and DARA computes
// the flags / variance band / auto-brief live. All state is local — no
// data leaves the browser. Designed so the partner arrives at the
// "this is real" conclusion from THEIR numbers, not ours.
// ─────────────────────────────────────────────────────────────────────

function classifySandboxFlag(pct) {
  if (pct >= 100) return { label: "LEGACY TARIFF · IMMEDIATE RENEGOTIATION", tone: "high" };
  if (pct >= 30) return { label: "TENDER WINDOW · MATERIAL OPPORTUNITY", tone: "high" };
  if (pct >= 15) return { label: "TARIFF MISMATCH · INVESTIGATE", tone: "watch" };
  if (pct >= 5) return { label: "BUNDLED USAGE DRIFT · MONITOR", tone: "watch" };
  if (pct >= -5) return { label: "IN LINE WITH BENCHMARK", tone: "ok" };
  return { label: "BELOW BENCHMARK · GOOD CONTRACT", tone: "good" };
}

function buildAutoBrief(rows, oppLow, oppHigh, partnerName) {
  const filled = rows.filter(r => r.spend > 0 && r.sites > 0);
  if (filled.length === 0) {
    return "Add at least one subsidiary above with last-12-month spend > A$0. DARA will write the rollup brief from your numbers.";
  }
  const overs = filled.filter(r => r.pct >= 15);
  if (overs.length === 0) {
    return `All ${filled.length} entered ${filled.length === 1 ? "entity is" : "entities are"} running within ±15 % of the benchmark on this cost line. No material opportunity flagged. Recommend monitoring quarterly and revisiting if any contract end-dates approach.`;
  }
  const worst = filled.reduce((a, b) => (b.pct > a.pct ? b : a));
  const lowest = Math.min(...overs.map(r => Math.round(r.pct)));
  const highest = Math.round(worst.pct);
  const range = lowest === highest ? `${lowest} %` : `${lowest}-${highest} %`;
  return `${partnerName} — Electricity Rollup · ${new Date().toLocaleString("en-AU", { month: "long", year: "numeric" })}. Of ${filled.length} operating ${filled.length === 1 ? "entity" : "entities"}, ${overs.length} ${overs.length === 1 ? "is" : "are"} running ${range} above DARA's benchmark. Largest opportunity: ${worst.name} (A$${worst.spend.toLocaleString("en-AU")} last 12 months) — ${worst.flag.label.toLowerCase()}. Headline savings band: A$${oppLow.toLocaleString("en-AU")}-A$${oppHigh.toLocaleString("en-AU")} annualised, depending on tender execution. Suggested next cost line for quarterly rollup: telco / mobile fleet.`;
}

function GroupRollupSandbox() {
  const [parentName, setParentName] = useState("Client Group · Pty Ltd");
  const [benchmarkPerSite, setBenchmarkPerSite] = useState(BENCHMARK_DEFAULT);
  const [rows, setRows] = useState(SANDBOX_SEED_ROWS);

  const addRow = () => {
    const id = `r-${Date.now()}`;
    setRows([...rows, { id, name: "New subsidiary", sites: 1, spend: 0 }]);
  };
  const removeRow = (id) => setRows(rows.filter(r => r.id !== id));
  const updateRow = (id, field, value) => {
    setRows(rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  };
  const resetSeed = () => {
    setRows(SANDBOX_SEED_ROWS);
    setParentName("Client Group · Pty Ltd");
    setBenchmarkPerSite(BENCHMARK_DEFAULT);
  };
  const clearAll = () => {
    setRows([{ id: `r-${Date.now()}`, name: "", sites: 1, spend: 0 }]);
  };

  const enriched = rows.map(r => {
    const benchmark = (Number(r.sites) || 0) * benchmarkPerSite;
    const spend = Number(r.spend) || 0;
    const delta = spend - benchmark;
    const pct = benchmark > 0 ? (delta / benchmark) * 100 : 0;
    const flag = classifySandboxFlag(pct);
    return { ...r, sites: Number(r.sites) || 0, spend, benchmark, delta, pct, flag };
  });

  const totalSpend = enriched.reduce((a, b) => a + b.spend, 0);
  const totalBench = enriched.reduce((a, b) => a + b.benchmark, 0);
  const positiveDelta = enriched.filter(r => r.delta > 0).reduce((a, b) => a + b.delta, 0);
  // Conservative savings band — 60-90 % of the over-benchmark excess is recoverable
  // in practice via tariff renegotiation + group tender (Burditt's own published
  // result range on the Hodgson electricity work).
  const oppLow = Math.round(positiveDelta * 0.60);
  const oppHigh = Math.round(positiveDelta * 0.90);
  const brief = buildAutoBrief(enriched, oppLow, oppHigh, parentName);

  // Phase 25.13as — DARA streams the brief out word-by-word whenever the
  // partner edits any input. Reinforces "this is DARA writing, live" rather
  // than reading pre-baked marketing copy. ~32ms per word ≈ comfortable
  // reading pace without feeling sluggish.
  const [displayedBrief, setDisplayedBrief] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const streamTokenRef = useRef(0);

  useEffect(() => {
    const tokens = brief.split(" ");
    const myToken = ++streamTokenRef.current;
    setIsStreaming(true);
    setDisplayedBrief("");
    let idx = 0;
    let timers = [];
    const tick = () => {
      if (streamTokenRef.current !== myToken) return;
      if (idx >= tokens.length) {
        setIsStreaming(false);
        return;
      }
      const next = idx === 0 ? tokens[idx] : ` ${tokens[idx]}`;
      setDisplayedBrief((prev) => prev + next);
      idx += 1;
      timers.push(setTimeout(tick, 32));
    };
    timers.push(setTimeout(tick, 120));
    return () => {
      // Invalidate this stream + clear every queued tick so stray timers
      // can't write to state after the partner has already edited again.
      streamTokenRef.current += 1;
      timers.forEach(clearTimeout);
      timers = [];
    };
  }, [brief]);

  return (
    <div className="mt-10 border border-zinc-900" data-testid="spi-sandbox">
      {/* Controls bar — group parent + benchmark slider */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border-b border-zinc-900">
        <div className="bg-black p-5">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">// CLIENT GROUP NAME</span>
            <input
              type="text"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              data-testid="spi-sandbox-parent"
              className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 font-heading font-bold text-base text-white focus:outline-none focus:border-[#FFB000]"
            />
          </label>
        </div>
        <div className="bg-black p-5 md:col-span-2">
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// DARA BENCHMARK · A$ PER SITE PER YEAR</span>
            <span className="font-heading font-black text-2xl text-[#FFB000] tracking-tighter">A${benchmarkPerSite.toLocaleString("en-AU")}</span>
          </div>
          <input
            type="range"
            min={BENCHMARK_MIN}
            max={BENCHMARK_MAX}
            step={500}
            value={benchmarkPerSite}
            onChange={(e) => setBenchmarkPerSite(Number(e.target.value))}
            data-testid="spi-sandbox-benchmark"
            className="w-full accent-[#FFB000] cursor-pointer"
            aria-label="DARA benchmark in AUD per site per year"
          />
          <div className="flex justify-between font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
            <span>A${BENCHMARK_MIN.toLocaleString("en-AU")} (low-load)</span>
            <span>A${BENCHMARK_DEFAULT.toLocaleString("en-AU")} (mid-market)</span>
            <span>A${BENCHMARK_MAX.toLocaleString("en-AU")} (heavy-load)</span>
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mt-2 leading-relaxed">
            Drag this until DARA&apos;s &quot;fair WA tariff&quot; line matches your view of the market. You know WA electricity better than the software does.
          </p>
        </div>
      </div>

      {/* Summary band */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-900 border-b border-zinc-900">
        <SummaryCell label="GROUP PARENT" value={parentName || "—"} accent />
        <SummaryCell label="TOTAL SPEND · LAST 12 MO" value={fmtAud(totalSpend)} />
        <SummaryCell label="DARA BENCHMARK" value={fmtAud(totalBench)} />
        <SummaryCell
          label="ANNUALISED OPPORTUNITY"
          value={oppHigh > 0 ? `${fmtAud(oppLow)} → ${fmtAud(oppHigh)}` : "— enter spend —"}
          highlight
        />
      </div>

      {/* Editable rows */}
      <div className="overflow-x-auto bg-black">
        <table className="w-full text-xs" data-testid="spi-sandbox-table">
          <thead>
            <tr className="bg-zinc-950 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <th className="text-left p-3 border-b border-zinc-900">Subsidiary</th>
              <th className="text-right p-3 border-b border-zinc-900">Sites</th>
              <th className="text-right p-3 border-b border-zinc-900">Spend · last 12 mo</th>
              <th className="text-right p-3 border-b border-zinc-900">DARA Benchmark</th>
              <th className="text-right p-3 border-b border-zinc-900">Δ</th>
              <th className="text-left p-3 border-b border-zinc-900">Flag</th>
              <th className="p-3 border-b border-zinc-900 w-10" />
            </tr>
          </thead>
          <tbody>
            {enriched.map((r, idx) => {
              const flagColour =
                r.flag.tone === "high" ? "text-[#FF6B47] border-[#FF6B47]/40 bg-[#FF6B47]/5"
                  : r.flag.tone === "watch" ? "text-[#FFB000] border-[#FFB000]/40 bg-[#FFB000]/5"
                  : r.flag.tone === "good" ? "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5"
                  : "text-zinc-500 border-zinc-700 bg-zinc-900";
              return (
                <tr key={r.id} className="border-b border-zinc-900" data-testid={`spi-sandbox-row-${idx}`}>
                  <td className="p-2">
                    <input
                      type="text"
                      value={r.name}
                      onChange={(e) => updateRow(r.id, "name", e.target.value)}
                      data-testid={`spi-sandbox-row-${idx}-name`}
                      placeholder="Subsidiary name"
                      className="w-full bg-transparent border border-transparent hover:border-zinc-800 focus:border-[#FFB000] px-2 py-1.5 text-zinc-100 focus:outline-none"
                    />
                  </td>
                  <td className="p-2 text-right">
                    <input
                      type="number"
                      min={0}
                      max={500}
                      value={r.sites}
                      onChange={(e) => updateRow(r.id, "sites", Math.max(0, Number(e.target.value) || 0))}
                      data-testid={`spi-sandbox-row-${idx}-sites`}
                      className="w-16 bg-transparent border border-transparent hover:border-zinc-800 focus:border-[#FFB000] px-2 py-1.5 text-right font-mono text-zinc-100 focus:outline-none"
                    />
                  </td>
                  <td className="p-2 text-right">
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      value={r.spend}
                      onChange={(e) => updateRow(r.id, "spend", Math.max(0, Number(e.target.value) || 0))}
                      data-testid={`spi-sandbox-row-${idx}-spend`}
                      className="w-32 bg-transparent border border-transparent hover:border-zinc-800 focus:border-[#FFB000] px-2 py-1.5 text-right font-mono text-zinc-100 focus:outline-none"
                    />
                  </td>
                  <td className="p-3 text-right font-mono text-zinc-400">{fmtAud(r.benchmark)}</td>
                  <td className={`p-3 text-right font-mono font-bold ${
                    r.benchmark === 0 ? "text-zinc-600" :
                    r.delta > 0 ? "text-[#FF6B47]" :
                    r.delta < 0 ? "text-[#00E676]" : "text-zinc-500"
                  }`}>
                    {r.benchmark === 0 ? "—" : `${r.delta > 0 ? "+" : ""}${r.pct.toFixed(1)} %`}
                  </td>
                  <td className="p-3">
                    {r.benchmark === 0 || r.spend === 0 ? (
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">— enter data —</span>
                    ) : (
                      <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${flagColour}`}>
                        {r.flag.label}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => removeRow(r.id)}
                      data-testid={`spi-sandbox-row-${idx}-remove`}
                      title="Remove subsidiary"
                      className="text-zinc-600 hover:text-[#FF6B47] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Row controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-zinc-900 bg-black">
        <button
          type="button"
          onClick={addRow}
          data-testid="spi-sandbox-add-row"
          className="font-mono text-[10px] uppercase tracking-widest border border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000] px-4 py-2 flex items-center gap-2"
        >
          <Plus className="w-3 h-3" /> Add subsidiary
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearAll}
            data-testid="spi-sandbox-clear"
            className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000]"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={resetSeed}
            data-testid="spi-sandbox-reset"
            className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000]"
          >
            Reset to defaults
          </button>
        </div>
      </div>

      {/* Auto-brief — streams from DARA word-by-word */}
      <div className="p-5 border-t border-zinc-900 bg-[#FFB000]/[0.04]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> // DARA AUTO-BRIEF · WRITTEN FROM YOUR NUMBERS
          {isStreaming && (
            <span className="ml-auto flex items-center gap-1.5 text-zinc-400 normal-case tracking-normal font-mono text-[10px]" data-testid="spi-sandbox-streaming">
              <span className="w-1.5 h-1.5 bg-[#FFB000] rounded-full animate-pulse" />
              DARA is writing…
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed min-h-[3em]" data-testid="spi-sandbox-brief">
          {displayedBrief}
          {isStreaming && (
            <span className="inline-block w-2 h-4 bg-[#FFB000] ml-1 align-middle animate-pulse" aria-hidden="true" />
          )}
        </p>
      </div>

      {/* Privacy + scope note */}
      <div className="p-4 border-t border-zinc-900 bg-zinc-950/40 flex items-start gap-3">
        <Info className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          All numbers above stay on this device. Nothing is sent to DARA, logged, or stored. <span className="text-zinc-400">Refresh the page and your inputs reset.</span> Once a real engagement starts, DARA reads the live data directly from each subsidiary&apos;s billing systems read-only — no spreadsheet typing required.
        </p>
      </div>
    </div>
  );
}

